// カスタムルール設定

import { CardTier } from './card';

// カスタムゲーム設定 (詳細設定)
export interface CustomGameConfig {
  // 基本ルール
  qualifyingRounds: number;        // 予選ラウンド数 (1-10)
  playerCount: number;             // プレイヤー数 (2-8)
  maxLosses: number;               // 敗退までの敗北数 (1-5)
  
  // デッキ構築
  startingDeckSize: number;        // 初期デッキ枚数 (4-10)
  maxDeckSize: number;             // デッキ上限 (15-50)
  benchLimit: number;              // ベンチ上限 (4-10)
  
  // カード取得
  tierDraws: Record<CardTier, number>[];  // ラウンドごとのティア取得枚数
  cardChoiceCount: number;         // 選択肢に表示するカード数 (2-5)
  
  // バトル
  timeLimit: number;               // ターン制限 (0=無制限, 1-100)
  enableAbilities: boolean;        // 能力を有効にする
  enableSynergies: boolean;        // シナジーを有効にする
  
  // ファン獲得
  baseFansPerWin: number;          // 勝利時基本ファン (1-10)
  fansMultiplier: number;          // ラウンドごとの倍率 (1.0-2.0)
  bonusFansForStreak: number;      // 連勝ボーナス (0-5)
  
  // 特殊ルール
  allowCardRemoval: boolean;       // カード削除を許可
  shuffleDeckEachRound: boolean;   // 毎ラウンドデッキシャッフル
  showOpponentDeck: boolean;       // 相手デッキを公開
  doubleElimination: boolean;      // ダブルイリミネーション
}

// デフォルト設定
export const DEFAULT_CUSTOM_CONFIG: CustomGameConfig = {
  qualifyingRounds: 7,
  playerCount: 8,
  maxLosses: 3,
  
  startingDeckSize: 6,
  maxDeckSize: 30,
  benchLimit: 6,
  
  tierDraws: [
    { A: 3, B: 0, C: 0 },  // Round 1
    { A: 2, B: 1, C: 0 },  // Round 2
    { A: 2, B: 2, C: 0 },  // Round 3
    { A: 1, B: 2, C: 0 },  // Round 4
    { A: 1, B: 2, C: 1 },  // Round 5
    { A: 0, B: 2, C: 1 },  // Round 6
    { A: 0, B: 1, C: 2 },  // Round 7
  ],
  cardChoiceCount: 3,
  
  timeLimit: 0,
  enableAbilities: true,
  enableSynergies: true,
  
  baseFansPerWin: 1,
  fansMultiplier: 1.0,
  bonusFansForStreak: 1,
  
  allowCardRemoval: true,
  shuffleDeckEachRound: true,
  showOpponentDeck: false,
  doubleElimination: false,
};

// プリセット設定
export interface RulePreset {
  id: string;
  name: string;
  description: string;
  icon: string;
  config: Partial<CustomGameConfig>;
}

export const RULE_PRESETS: RulePreset[] = [
  {
    id: 'standard',
    name: 'スタンダード',
    description: '標準ルール。8人で7ラウンドの予選を戦い、決勝へ進出。',
    icon: '⚖️',
    config: {},
  },
  {
    id: 'quick',
    name: 'クイックマッチ',
    description: '短時間で決着。4人で3ラウンド、2敗で脱落。',
    icon: '⚡',
    config: {
      qualifyingRounds: 3,
      playerCount: 4,
      maxLosses: 2,
      tierDraws: [
        { A: 2, B: 1, C: 0 },
        { A: 1, B: 2, C: 1 },
        { A: 0, B: 1, C: 2 },
      ],
    },
  },
  {
    id: 'marathon',
    name: 'マラソン',
    description: '長期戦。10ラウンド、大量のカードでデッキ構築。',
    icon: '🏃',
    config: {
      qualifyingRounds: 10,
      maxLosses: 4,
      maxDeckSize: 50,
      tierDraws: [
        { A: 4, B: 0, C: 0 },
        { A: 3, B: 1, C: 0 },
        { A: 3, B: 2, C: 0 },
        { A: 2, B: 2, C: 0 },
        { A: 2, B: 2, C: 1 },
        { A: 1, B: 3, C: 1 },
        { A: 1, B: 2, C: 2 },
        { A: 0, B: 3, C: 2 },
        { A: 0, B: 2, C: 3 },
        { A: 0, B: 1, C: 4 },
      ],
    },
  },
  {
    id: 'chaos',
    name: 'カオス',
    description: '予測不能。全ティアランダム、相手デッキ公開。',
    icon: '🌀',
    config: {
      showOpponentDeck: true,
      tierDraws: [
        { A: 1, B: 1, C: 1 },
        { A: 1, B: 1, C: 1 },
        { A: 1, B: 1, C: 1 },
        { A: 1, B: 1, C: 1 },
        { A: 1, B: 1, C: 1 },
        { A: 1, B: 1, C: 1 },
        { A: 1, B: 1, C: 1 },
      ],
    },
  },
  {
    id: 'powerUp',
    name: 'パワーアップ',
    description: '高パワーカード中心。Tier Cを多めに取得。',
    icon: '💪',
    config: {
      tierDraws: [
        { A: 1, B: 1, C: 1 },
        { A: 1, B: 1, C: 1 },
        { A: 0, B: 2, C: 1 },
        { A: 0, B: 1, C: 2 },
        { A: 0, B: 1, C: 2 },
        { A: 0, B: 0, C: 3 },
        { A: 0, B: 0, C: 3 },
      ],
    },
  },
  {
    id: 'basic',
    name: 'ベーシック',
    description: '能力なし、シンプルなパワー勝負。',
    icon: '📦',
    config: {
      enableAbilities: false,
      enableSynergies: false,
    },
  },
  {
    id: 'survival',
    name: 'サバイバル',
    description: '1敗で即脱落。緊張感のある戦い。',
    icon: '☠️',
    config: {
      maxLosses: 1,
      baseFansPerWin: 2,
    },
  },
  {
    id: 'duel',
    name: 'デュエル',
    description: '1対1の真剣勝負。5ラウンド先取。',
    icon: '⚔️',
    config: {
      playerCount: 2,
      qualifyingRounds: 5,
      maxLosses: 3,
      tierDraws: [
        { A: 2, B: 1, C: 0 },
        { A: 2, B: 1, C: 1 },
        { A: 1, B: 2, C: 1 },
        { A: 0, B: 2, C: 2 },
        { A: 0, B: 1, C: 3 },
      ],
    },
  },
];

// 設定のバリデーション
export function validateCustomConfig(config: Partial<CustomGameConfig>): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (config.qualifyingRounds !== undefined) {
    if (config.qualifyingRounds < 1 || config.qualifyingRounds > 10) {
      errors.push('予選ラウンド数は1-10の範囲で設定してください');
    }
  }
  
  if (config.playerCount !== undefined) {
    if (config.playerCount < 2 || config.playerCount > 8) {
      errors.push('プレイヤー数は2-8の範囲で設定してください');
    }
  }
  
  if (config.maxLosses !== undefined) {
    if (config.maxLosses < 1 || config.maxLosses > 5) {
      errors.push('敗退条件は1-5敗の範囲で設定してください');
    }
  }
  
  if (config.startingDeckSize !== undefined) {
    if (config.startingDeckSize < 4 || config.startingDeckSize > 10) {
      errors.push('初期デッキ枚数は4-10の範囲で設定してください');
    }
  }
  
  if (config.benchLimit !== undefined) {
    if (config.benchLimit < 4 || config.benchLimit > 10) {
      errors.push('ベンチ上限は4-10の範囲で設定してください');
    }
  }
  
  return { valid: errors.length === 0, errors };
}

// 設定をマージ
export function mergeCustomConfig(base: CustomGameConfig, override: Partial<CustomGameConfig>): CustomGameConfig {
  return {
    ...base,
    ...override,
    tierDraws: override.tierDraws ?? base.tierDraws,
  };
}

// プリセットから設定を取得
export function getPresetConfig(presetId: string): CustomGameConfig {
  const preset = RULE_PRESETS.find((p) => p.id === presetId);
  if (!preset) return DEFAULT_CUSTOM_CONFIG;
  return mergeCustomConfig(DEFAULT_CUSTOM_CONFIG, preset.config);
}

// ラウンドごとのティア取得枚数を取得
export function getTierDrawsForRound(config: CustomGameConfig, round: number): Record<CardTier, number> {
  const index = Math.min(round - 1, config.tierDraws.length - 1);
  return config.tierDraws[index] ?? { A: 1, B: 1, C: 0 };
}

// ファン獲得量を計算
export function calculateFansGained(
  config: CustomGameConfig,
  round: number,
  streak: number
): number {
  const base = config.baseFansPerWin;
  const multiplier = Math.pow(config.fansMultiplier, round - 1);
  const streakBonus = streak >= 3 ? config.bonusFansForStreak : 0;
  
  return Math.floor(base * multiplier) + streakBonus;
}
